import React, { useState } from "react";
import { ADMIN_OPTIONS, DEFAULT_ADMIN } from "../config/admins";
import { getWinelabelCredits, renewCredits, UserCredit } from "../api/partner";

interface RenewClientProps {
  onBack: () => void;
}

export default function RenewClient({ onBack }: RenewClientProps) {
  const [adminUuid, setAdminUuid] = useState(DEFAULT_ADMIN.uuid);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserCredit[]>([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [editValues, setEditValues] = useState<Record<string, number>>({});
  const [updating, setUpdating] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const handleSearch = async () => {
    setError("");
    setUsers([]);
    setFeedback(null);
    setEditValues({});
    setLoading(true);

    try {
      const result = await getWinelabelCredits(adminUuid);
      if (result.ok && result.data) {
        setUsers(result.data);
      } else {
        setError(result.message || "Erreur lors de la recherche");
      }
    } catch (err: any) {
      setError(err.message || "Erreur reseau");
    } finally {
      setLoading(false);
    }
  };

  const handleRenew = async (user: UserCredit) => {
    const newValue = editValues[user.user_id];
    if (newValue === undefined || newValue === user.credits_bought) return;

    setFeedback(null);
    setUpdating(user.user_id);

    try {
      const result = await renewCredits(adminUuid, user.user_id, newValue);
      if (result.ok) {
        const delta = newValue - user.credits_bought;
        const sign = delta > 0 ? "+" : "";
        setFeedback({
          type: "success",
          message: `${user.email} : ${sign}${delta} credits (nouveau total : ${newValue})`,
        });
        // Refresh the list
        handleSearch();
      } else {
        setFeedback({ type: "error", message: result.message || "Erreur lors de la mise a jour" });
      }
    } catch (err: any) {
      setFeedback({ type: "error", message: err.message || "Erreur reseau" });
    } finally {
      setUpdating(null);
    }
  };

  const filtered = users.filter(
    (u) => u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <button onClick={onBack} style={styles.backBtn}>
            &larr; Retour
          </button>
          <h2 style={styles.title}>Renouveler les credits d'un client</h2>
        </div>

        <div style={styles.searchRow}>
          <select
            value={adminUuid}
            onChange={(e) => setAdminUuid(e.target.value)}
            style={styles.select}
          >
            {ADMIN_OPTIONS.map((admin) => (
              <option key={admin.uuid} value={admin.uuid}>
                {admin.name}
              </option>
            ))}
          </select>
          <button onClick={handleSearch} disabled={loading} style={styles.searchBtn}>
            {loading ? "Chargement..." : "Charger les clients"}
          </button>
        </div>

        {error && <p style={styles.error}>{error}</p>}
        {feedback && (
          <p style={feedback.type === "success" ? styles.success : styles.error}>
            {feedback.message}
          </p>
        )}

        {users.length > 0 && (
          <>
            <input
              type="text"
              placeholder="Rechercher par email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={styles.filterInput}
            />
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Email</th>
                    <th style={styles.thCenter}>Achetes</th>
                    <th style={styles.thCenter}>Utilises</th>
                    <th style={styles.thCenter}>Restants</th>
                    <th style={styles.thCenter}>Nouveau total</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((user) => {
                    const editVal = editValues[user.user_id];
                    const hasChange = editVal !== undefined && editVal !== user.credits_bought;
                    return (
                      <tr key={user.user_id}>
                        <td style={styles.td}>{user.email}</td>
                        <td style={styles.tdCenter}>{user.credits_bought}</td>
                        <td style={styles.tdCenter}>{user.credits_used}</td>
                        <td style={styles.tdCenter}>{user.credits_left}</td>
                        <td style={styles.tdCenter}>
                          <input
                            type="number"
                            min={0}
                            value={editVal !== undefined ? editVal : user.credits_bought}
                            onChange={(e) =>
                              setEditValues((prev) => ({
                                ...prev,
                                [user.user_id]: parseInt(e.target.value) || 0,
                              }))
                            }
                            style={styles.creditInput}
                          />
                        </td>
                        <td style={styles.td}>
                          <button
                            style={hasChange ? styles.renewBtn : styles.renewBtnDisabled}
                            disabled={!hasChange || updating === user.user_id}
                            onClick={() => handleRenew(user)}
                          >
                            {updating === user.user_id ? "..." : "Appliquer"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={6} style={styles.empty}>
                        Aucun client trouve pour "{search}"
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <p style={styles.hint}>
              {filtered.length} client{filtered.length !== 1 ? "s" : ""} sur {users.length}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
    padding: "40px 16px",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: 40,
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    width: "100%",
    maxWidth: 900,
  },
  header: { marginBottom: 24 },
  backBtn: {
    background: "none",
    border: "none",
    color: "#4f46e5",
    fontSize: 14,
    cursor: "pointer",
    padding: 0,
    marginBottom: 8,
  },
  title: { margin: 0, fontSize: 22, color: "#333" },
  searchRow: { display: "flex", gap: 12, marginBottom: 16 },
  select: {
    flex: 1,
    padding: "10px 14px",
    border: "1px solid #ddd",
    borderRadius: 6,
    fontSize: 14,
    backgroundColor: "#fff",
  },
  searchBtn: {
    padding: "10px 20px",
    backgroundColor: "#4f46e5",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  filterInput: {
    width: "100%",
    padding: "10px 14px",
    border: "1px solid #ddd",
    borderRadius: 6,
    fontSize: 14,
    marginBottom: 12,
    boxSizing: "border-box" as const,
  },
  tableWrapper: { overflowX: "auto" as const },
  table: {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontSize: 13,
  },
  th: {
    textAlign: "left" as const,
    padding: "8px 10px",
    borderBottom: "2px solid #e5e7eb",
    color: "#6b7280",
    fontWeight: 600,
    fontSize: 12,
    whiteSpace: "nowrap" as const,
  },
  thCenter: {
    textAlign: "center" as const,
    padding: "8px 10px",
    borderBottom: "2px solid #e5e7eb",
    color: "#6b7280",
    fontWeight: 600,
    fontSize: 12,
    whiteSpace: "nowrap" as const,
  },
  td: {
    padding: "8px 10px",
    borderBottom: "1px solid #f3f4f6",
  },
  tdCenter: {
    padding: "8px 10px",
    borderBottom: "1px solid #f3f4f6",
    textAlign: "center" as const,
  },
  creditInput: {
    width: 70,
    padding: "4px 8px",
    border: "1px solid #ddd",
    borderRadius: 4,
    fontSize: 13,
    textAlign: "center" as const,
  },
  renewBtn: {
    padding: "4px 12px",
    backgroundColor: "#4f46e5",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
  },
  renewBtnDisabled: {
    padding: "4px 12px",
    backgroundColor: "#e5e7eb",
    color: "#9ca3af",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    cursor: "not-allowed",
    whiteSpace: "nowrap" as const,
  },
  error: {
    color: "#dc2626",
    fontSize: 13,
    margin: "0 0 12px",
    padding: "8px 12px",
    backgroundColor: "#fef2f2",
    borderRadius: 6,
  },
  success: {
    color: "#16a34a",
    fontSize: 13,
    margin: "0 0 12px",
    padding: "8px 12px",
    backgroundColor: "#f0fdf4",
    borderRadius: 6,
  },
  empty: {
    textAlign: "center" as const,
    padding: 20,
    color: "#9ca3af",
  },
  hint: {
    fontSize: 12,
    color: "#9ca3af",
    marginTop: 8,
  },
};
